import React, { useCallback, useEffect, useRef, useState } from "react";
import { RefreshCw, Link2, Link2Off, CheckCircle2, AlertCircle, Wifi } from "lucide-react";
import { Button } from "../../ui/button";
import LoadingSpinner from "../../loading-spinner/LoadingSpinner";
import { useProfileSetup } from "../../../contexts/ProfileContext";
import { cn } from "../../../lib/utils";

/**
 * Admin-only Vibe Prospecting connection card.
 *
 * Behaviour:
 *  - Fetches status on mount and on "Refresh status" click.
 *  - "Connect Vibe" opens a popup window to the Vibe OAuth authorization URL
 *    returned by the backend. Listens for a postMessage from the callback HTML
 *    page and re-fetches status on success.
 *  - "Disconnect" shows an inline confirmation then calls DELETE.
 *  - Never displays or stores tokens.
 */
export default function VibeConnectTab() {
  const { apiBase, getAuthHeaders } = useProfileSetup();

  const [status, setStatus] = useState(null); // null | { connected, status, connectedAt, connectedByEmail, expiresAt, scope }
  const [loading, setLoading] = useState(true);
  const [connectLoading, setConnectLoading] = useState(false);
  const [disconnectLoading, setDisconnectLoading] = useState(false);
  const [confirmDisconnect, setConfirmDisconnect] = useState(false);
  const [error, setError] = useState(null);

  const popupRef = useRef(null);
  const messageListenerRef = useRef(null);

  // ─── Fetch status ───────────────────────────────────────────────────────────

  const fetchStatus = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const headers = await getAuthHeaders();
      const resp = await fetch(`${apiBase}/admin/vibe/status`, { headers });
      if (!resp.ok) {
        const body = await resp.json().catch(() => ({}));
        throw new Error(body.error || `Status check failed (${resp.status})`);
      }
      const data = await resp.json();
      setStatus(data);
    } catch (err) {
      setError(err.message || "Failed to fetch Vibe status");
    } finally {
      setLoading(false);
    }
  }, [apiBase, getAuthHeaders]);

  useEffect(() => {
    fetchStatus();
  }, [fetchStatus]);

  // Cleanup popup listener on unmount
  useEffect(() => {
    return () => {
      if (messageListenerRef.current) {
        window.removeEventListener("message", messageListenerRef.current);
      }
    };
  }, []);

  // ─── Connect flow ───────────────────────────────────────────────────────────

  const handleConnect = async () => {
    setConnectLoading(true);
    setError(null);
    try {
      const headers = await getAuthHeaders();
      const resp = await fetch(`${apiBase}/admin/vibe/connect/start`, {
        method: "POST",
        headers: { ...headers, "Content-Type": "application/json" },
      });
      if (!resp.ok) {
        const body = await resp.json().catch(() => ({}));
        throw new Error(body.error || `Failed to start OAuth (${resp.status})`);
      }
      const { authorizationUrl } = await resp.json();

      // Open the Vibe authorization URL in a popup window
      const popup = window.open(
        authorizationUrl,
        "vibe-oauth-popup",
        "width=600,height=700,scrollbars=yes,resizable=yes"
      );
      popupRef.current = popup;

      // Resolve the expected callback origin from apiBase
      // The backend callback page posts from the API's origin
      let callbackOrigin;
      try {
        callbackOrigin = new URL(apiBase).origin;
      } catch {
        callbackOrigin = null;
      }

      // Remove any previous listener
      if (messageListenerRef.current) {
        window.removeEventListener("message", messageListenerRef.current);
      }

      const onMessage = (event) => {
        // Validate origin — only accept messages from the API's origin
        if (callbackOrigin && event.origin !== callbackOrigin) return;

        const data = event.data;
        if (!data || data.source !== "vibe-oauth") return;

        // Clean up
        window.removeEventListener("message", onMessage);
        messageListenerRef.current = null;
        if (popupRef.current && !popupRef.current.closed) {
          popupRef.current.close();
        }
        popupRef.current = null;

        setConnectLoading(false);
        if (data.success) {
          fetchStatus();
        } else {
          setError(data.message || "Vibe connection failed");
        }
      };

      messageListenerRef.current = onMessage;
      window.addEventListener("message", onMessage);

      // Fallback: if the popup is closed without a postMessage (user closed manually)
      const pollClosed = setInterval(() => {
        if (popup && popup.closed) {
          clearInterval(pollClosed);
          if (messageListenerRef.current) {
            window.removeEventListener("message", messageListenerRef.current);
            messageListenerRef.current = null;
          }
          setConnectLoading(false);
          // Re-fetch status in case auth succeeded but postMessage was missed
          fetchStatus();
        }
      }, 800);
    } catch (err) {
      setConnectLoading(false);
      setError(err.message || "Failed to start Vibe connection");
    }
  };

  // ─── Disconnect flow ────────────────────────────────────────────────────────

  const handleDisconnect = async () => {
    setDisconnectLoading(true);
    setError(null);
    try {
      const headers = await getAuthHeaders();
      const resp = await fetch(`${apiBase}/admin/vibe/disconnect`, {
        method: "DELETE",
        headers,
      });
      if (!resp.ok) {
        const body = await resp.json().catch(() => ({}));
        throw new Error(body.error || `Disconnect failed (${resp.status})`);
      }
      setConfirmDisconnect(false);
      await fetchStatus();
    } catch (err) {
      setError(err.message || "Failed to disconnect Vibe");
    } finally {
      setDisconnectLoading(false);
    }
  };

  // ─── Helpers ─────────────────────────────────────────────────────────────────

  function formatDate(isoString) {
    if (!isoString) return null;
    try {
      return new Date(isoString).toLocaleString();
    } catch {
      return isoString;
    }
  }

  function formatExpiry(epochSec) {
    if (!epochSec) return null;
    try {
      return new Date(epochSec * 1000).toLocaleString();
    } catch {
      return null;
    }
  }

  // ─── Render ───────────────────────────────────────────────────────────────────

  const connectionStatus = status?.status ?? "not_connected";

  const statusConfig = {
    connected: {
      icon: <CheckCircle2 className="h-5 w-5 text-green-500" />,
      label: "Connected",
      labelClass: "text-green-600 dark:text-green-400",
    },
    expired: {
      icon: <AlertCircle className="h-5 w-5 text-yellow-500" />,
      label: "Token expired",
      labelClass: "text-yellow-600 dark:text-yellow-400",
    },
    not_connected: {
      icon: <Wifi className="h-5 w-5 text-muted-foreground" />,
      label: "Not connected",
      labelClass: "text-muted-foreground",
    },
  };

  const cfg = statusConfig[connectionStatus] ?? statusConfig.not_connected;

  return (
    <div className="mt-4 space-y-4">
      <div className="rounded-lg border border-border bg-card p-6 space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-base font-semibold">Vibe Prospecting</h2>
            <p className="text-sm text-muted-foreground mt-0.5">
              One shared connection used by all app users for contact data enrichment.
            </p>
          </div>
          <Button
            variant="ghost"
            size="icon"
            onClick={fetchStatus}
            disabled={loading}
            title="Refresh status"
            aria-label="Refresh Vibe status"
          >
            <RefreshCw className={cn("h-4 w-4", loading && "animate-spin")} />
          </Button>
        </div>

        {/* Status indicator */}
        {loading ? (
          <div className="flex justify-center py-4">
            <LoadingSpinner />
          </div>
        ) : (
          <div className="flex items-center gap-2">
            {cfg.icon}
            <span className={cn("text-sm font-medium", cfg.labelClass)}>
              {cfg.label}
            </span>
          </div>
        )}

        {/* Connected metadata */}
        {!loading && connectionStatus === "connected" && status && (
          <dl className="grid grid-cols-1 gap-2 text-sm sm:grid-cols-2">
            {status.connectedByEmail && (
              <>
                <dt className="text-muted-foreground">Connected by</dt>
                <dd className="font-medium">{status.connectedByEmail}</dd>
              </>
            )}
            {status.connectedAt && (
              <>
                <dt className="text-muted-foreground">Connected at</dt>
                <dd className="font-medium">{formatDate(status.connectedAt)}</dd>
              </>
            )}
            {status.expiresAt && (
              <>
                <dt className="text-muted-foreground">Token expires</dt>
                <dd className="font-medium">{formatExpiry(status.expiresAt)}</dd>
              </>
            )}
            {status.scope && (
              <>
                <dt className="text-muted-foreground">Scope</dt>
                <dd className="font-medium">{status.scope}</dd>
              </>
            )}
          </dl>
        )}

        {/* Error message */}
        {error && (
          <p className="text-sm text-destructive bg-destructive/10 rounded-md px-3 py-2">
            {error}
          </p>
        )}

        {/* Actions */}
        {!loading && (
          <div className="flex flex-wrap gap-3">
            {connectionStatus !== "connected" && (
              <Button
                onClick={handleConnect}
                disabled={connectLoading}
                className="flex items-center gap-2"
              >
                {connectLoading ? (
                  <LoadingSpinner size="sm" />
                ) : (
                  <Link2 className="h-4 w-4" />
                )}
                {connectLoading ? "Connecting…" : "Connect Vibe"}
              </Button>
            )}

            {connectionStatus === "connected" && !confirmDisconnect && (
              <Button
                variant="outline"
                onClick={() => setConfirmDisconnect(true)}
                className="flex items-center gap-2"
              >
                <Link2Off className="h-4 w-4" />
                Disconnect
              </Button>
            )}

            {confirmDisconnect && (
              <div className="flex items-center gap-3 rounded-md border border-destructive/40 bg-destructive/5 px-4 py-2">
                <span className="text-sm text-destructive font-medium">
                  Remove the shared Vibe connection?
                </span>
                <Button
                  variant="destructive"
                  size="sm"
                  onClick={handleDisconnect}
                  disabled={disconnectLoading}
                >
                  {disconnectLoading ? "Disconnecting…" : "Yes, disconnect"}
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setConfirmDisconnect(false)}
                  disabled={disconnectLoading}
                >
                  Cancel
                </Button>
              </div>
            )}

            {connectionStatus === "expired" && (
              <Button
                onClick={handleConnect}
                disabled={connectLoading}
                className="flex items-center gap-2"
              >
                {connectLoading ? <LoadingSpinner size="sm" /> : <Link2 className="h-4 w-4" />}
                {connectLoading ? "Reconnecting…" : "Reconnect Vibe"}
              </Button>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
